import PoPillCellRenderer from "@/features/results/PoPillCellRenderer.vue";
import type { MovementGridRow } from "@/redistribution/models/MovementGridRow.js";
import type { ColDef } from "ag-grid-community";

/**
 * Render the descriptor cell with item details (SKU, description, collection, color)
 */
function renderDescriptorCell(parameters: {
  value: string | undefined;
  data?: MovementGridRow;
}): HTMLElement {
  const containerEl = document.createElement("div");
  containerEl.className = "item-movements-descriptor";

  const descriptionEl = document.createElement("span");
  descriptionEl.className = "item-movements-descriptor-primary";
  descriptionEl.textContent =
    parameters.value ?? parameters.data?.description ?? "—";

  const metadataEl = document.createElement("span");
  metadataEl.className = "item-movements-descriptor-secondary";

  const collection = parameters.data?.collection;
  const color = parameters.data?.color;
  metadataEl.textContent = collection
    ? color
      ? `${collection} (${color})`
      : collection
    : color
      ? `(${color})`
      : "—";

  containerEl.append(descriptionEl, metadataEl);
  return containerEl;
}

/**
 * Render the reason cell with WOS context
 */
function renderReasonCell(parameters: {
  value: string | undefined;
  data?: MovementGridRow;
}): HTMLElement | string {
  const spanEl = document.createElement("span");

  if (!parameters.value) return "";

  const data = parameters.data ?? ({} as MovementGridRow);

  switch (parameters.value) {
    case "CriticalRisk": {
      spanEl.className = "item-movements-reason-critical-risk";
      spanEl.textContent = "Prevent Critical Stockout Risk";
      const criticalDays =
        data.daysToStockoutBefore == null
          ? "?"
          : String(Math.round(data.daysToStockoutBefore));
      const criticalThreshold = String(data.rateOfSaleDays ?? 30);
      spanEl.title = `Days of stock before move: ${criticalDays} days (critically below ${criticalThreshold}-day threshold)`;
      break;
    }
    case "ModerateRisk": {
      spanEl.className = "item-movements-reason-moderate-risk";
      spanEl.textContent = "Prevent Moderate Stockout Risk";
      const shortageDays =
        data.daysToStockoutBefore == null
          ? "?"
          : String(Math.round(data.daysToStockoutBefore));
      const shortageThreshold = String(data.rateOfSaleDays ?? 30);
      spanEl.title = `Days of stock before move: ${shortageDays} days (below ${shortageThreshold}-day threshold)`;
      break;
    }
    case "BalanceWos": {
      spanEl.className = "item-movements-reason-balance-wos";
      spanEl.textContent = "Balance WOS";
      const before =
        data.wosBefore == null ? "?" : `${data.wosBefore.toFixed(1)}w`;
      const avg =
        data.wosAverage == null ? "—" : `${data.wosAverage.toFixed(1)}w`;
      spanEl.title =
        `WOS before move: ${before}, warehouse-network WOS target: ${avg} — ` +
        `destination was above stockout threshold but below the leveling target ` +
        `(avg days of stock across selected avg-WOS warehouses, using this run's demand ROS)`;
      break;
    }
    default: {
      spanEl.className = "item-movements-reason-makeroom";
      spanEl.textContent = "Make Room";
      spanEl.title =
        "Source PO exceeded cube capacity — stock moved to make room";
      break;
    }
  }
  return spanEl;
}

/**
 * Render WOS change as gain/loss with colored badge
 */
function renderWosChangeCell(parameters: {
  value: number | null | undefined;
}): string | HTMLElement {
  if (parameters.value == null) return "—";
  const spanEl = document.createElement("span");
  const weeks = parameters.value;
  spanEl.className =
    weeks >= 0 ? "item-movements-wos-gain" : "item-movements-wos-loss";
  spanEl.textContent = `${weeks >= 0 ? "+" : ""}${weeks.toFixed(1)}w`;
  return spanEl;
}

/**
 * Create column definitions for the Item Movements grid
 */
export function useItemMovementsGridColumns(): ColDef[] {
  return [
    {
      field: "sku",
      headerName: "SKU",
      cellRenderer: (parameters: { value: string }) => {
        const strongEl = document.createElement("strong");
        strongEl.textContent = parameters.value;
        return strongEl;
      },
      flex: 0.8,
      minWidth: 70,
    },
    {
      field: "description",
      headerName: "Item\nDetails",
      flex: 2,
      minWidth: 120,
      cellClass: "item-movements-descriptor-cell",
      cellRenderer: renderDescriptorCell,
      filter: "agTextColumnFilter",
    },
    {
      field: "from",
      headerName: "From\nPO",
      flex: 0.9,
      minWidth: 72,
      cellRenderer: PoPillCellRenderer,
      cellRendererParams: { variant: "from" },
      filter: "agTextColumnFilter",
    },
    {
      field: "to",
      headerName: "To\nPO",
      flex: 0.9,
      minWidth: 72,
      cellRenderer: PoPillCellRenderer,
      cellRendererParams: { variant: "to" },
      filter: "agTextColumnFilter",
    },
    {
      field: "qty",
      headerName: "Qty\nMoved",
      flex: 0.6,
      minWidth: 60,
      cellStyle: { fontWeight: "700", fontSize: "16px" },
      filter: "agNumberColumnFilter",
    },
    {
      field: "reason",
      headerName: "Move\nReason",
      flex: 1.2,
      minWidth: 100,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["equals", "contains", "notContains"],
        defaultOption: "equals",
        buttons: ["reset"],
      },
      cellRenderer: renderReasonCell,
    },
    {
      field: "wosBefore",
      headerName: "WOS\nBefore",
      flex: 0.7,
      minWidth: 65,
      filter: "agNumberColumnFilter",
      valueFormatter: (params: { value: number | null | undefined }) =>
        params.value == null ? "—" : `${params.value.toFixed(1)}w`,
    },
    {
      field: "wosChange",
      headerName: "WOS\nChange",
      flex: 0.7,
      minWidth: 65,
      filter: "agNumberColumnFilter",
      cellRenderer: renderWosChangeCell,
    },
    {
      field: "wosAfter",
      headerName: "WOS\nAfter",
      flex: 0.7,
      minWidth: 65,
      filter: "agNumberColumnFilter",
      valueFormatter: (params: { value: number | null | undefined }) =>
        params.value == null ? "—" : `${params.value.toFixed(1)}w`,
    },
    {
      field: "wosAverage",
      headerName: "WOS\nAverage",
      headerTooltip:
        "Warehouse-network WOS leveling target (weeks): mean days of stock across selected avg-WOS warehouses, rescaled to this run's demand ROS. Not the mean of selected PO-line WOS. Em dash when the SKU has no target.",
      flex: 0.7,
      minWidth: 65,
      filter: "agNumberColumnFilter",
      valueFormatter: (params: { value: number | null | undefined }) =>
        params.value == null ? "—" : `${params.value.toFixed(1)}w`,
    },
    {
      field: "daysToStockoutBefore",
      headerName: "Days to Stockout Before",
      flex: 1,
      minWidth: 60,
      hide: true,
      filter: "agNumberColumnFilter",
      valueFormatter: (params: { value: number | null | undefined }) =>
        params.value == null ? "—" : Math.round(params.value).toString(),
    },
    {
      field: "daysToStockoutAfter",
      headerName: "Days to Stockout After",
      flex: 1,
      minWidth: 60,
      hide: true,
      filter: "agNumberColumnFilter",
      valueFormatter: (params: { value: number | null | undefined }) =>
        params.value == null ? "—" : Math.round(params.value).toString(),
    },
  ];
}
